import {invalidateOnSuccess, metadaApi} from '@dms/api/base';

import {RECEIVE_NOTE_BASKET_ALL_TAGS} from '../metadaWarehouseReceiveNote/tags';
import {getBasketCheckoutTags, getDeleteBasketTags} from './invalidations';
import {normalizeBasketItems, normalizeBasketTotalPrice} from './normalizeBasket';
import {
  BulkDeleteReceiveNoteBasketItemsRequest,
  BulkDeleteReceiveNoteBasketItemsRequestSchema,
  BulkDeleteReceiveNoteBasketItemsResponse,
  BulkDeleteReceiveNoteBasketItemsResponseSchema,
  DeleteReceiveNoteBasketRequest,
  DeleteReceiveNoteBasketRequestSchema,
  DeleteReceiveNoteBasketResponse,
  DeleteReceiveNoteBasketResponseSchema,
  GetReceiveNoteBasketRequest,
  GetReceiveNoteBasketRequestSchema,
  GetReceiveNoteBasketResponse,
  GetReceiveNoteBasketResponseSchema,
  NormalizedBasketItems,
  NormalizedBasketTotalPrice,
  PatchReceiveNoteBasketItemQuantityRequest,
  PatchReceiveNoteBasketItemQuantityRequestSchema,
  PatchReceiveNoteBasketItemQuantityResponse,
  PatchReceiveNoteBasketItemQuantityResponseSchema,
  PostReceiveNoteBasketCheckoutRequest,
  PostReceiveNoteBasketCheckoutRequestSchema,
  PostReceiveNoteBasketCheckoutResponse,
  PostReceiveNoteBasketCheckoutResponseSchema,
  PostReceiveNoteBasketItemRequest,
  PostReceiveNoteBasketItemRequestSchema,
  PostReceiveNoteBasketItemResponse,
  PostReceiveNoteBasketItemResponseSchema,
} from './types';

export const metadaWarehouseReceiveNoteBasketApi = metadaApi.injectEndpoints({
  endpoints: (build) => ({
    getReceiveNoteBasketItems: build.query<NormalizedBasketItems, GetReceiveNoteBasketRequest>({
      query: (queryArg) => ({
        url: `warehouse/v1/receive-note/${queryArg.receiveNoteId}/basket`,
        method: 'GET',
        queryArg,
      }),
      transformResponse: (response: GetReceiveNoteBasketResponse) => normalizeBasketItems(response),
      providesTags: (result, error, queryArg) => [
        {type: 'receiveNoteBasketItems', id: queryArg.receiveNoteId},
      ],
      extraOptions: {
        requestSchema: GetReceiveNoteBasketRequestSchema,
        responseSchema: GetReceiveNoteBasketResponseSchema,
      },
    }),
    getReceiveNoteBasketTotalPrice: build.query<
      NormalizedBasketTotalPrice,
      GetReceiveNoteBasketRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v1/receive-note/${queryArg.receiveNoteId}/basket`,
        method: 'GET',
        queryArg,
      }),
      transformResponse: (response: GetReceiveNoteBasketResponse) =>
        normalizeBasketTotalPrice(response),
      providesTags: (result, error, queryArg) => [
        {type: 'receiveNoteBasketTotalPrice', id: queryArg.receiveNoteId},
      ],
      extraOptions: {
        requestSchema: GetReceiveNoteBasketRequestSchema,
        responseSchema: GetReceiveNoteBasketResponseSchema,
      },
    }),
    postReceiveNoteBasketCheckout: build.mutation<
      PostReceiveNoteBasketCheckoutResponse,
      PostReceiveNoteBasketCheckoutRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v1/receive-note/${queryArg.receiveNoteId}/basket/checkout`,
        method: 'POST',
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess(
        (result, queryArg: PostReceiveNoteBasketCheckoutRequest) =>
          getBasketCheckoutTags(queryArg.receiveNoteId)
      ),

      extraOptions: {
        requestSchema: PostReceiveNoteBasketCheckoutRequestSchema,
        responseSchema: PostReceiveNoteBasketCheckoutResponseSchema,
      },
    }),
    postReceiveNoteBasketItem: build.mutation<
      PostReceiveNoteBasketItemResponse,
      PostReceiveNoteBasketItemRequest
    >({
      query: (queryArg) => ({
        url: 'warehouse/v2/receive-note-item/manual',
        method: 'POST',
        queryArg,
        body: queryArg.body,
      }),
      invalidatesTags: (result, error, queryArg) => [
        ...RECEIVE_NOTE_BASKET_ALL_TAGS(queryArg.body.receiveNoteId),
      ],
      extraOptions: {
        requestSchema: PostReceiveNoteBasketItemRequestSchema,
        responseSchema: PostReceiveNoteBasketItemResponseSchema,
      },
    }),
    patchReceiveNoteBasketItemQuantity: build.mutation<
      PatchReceiveNoteBasketItemQuantityResponse,
      PatchReceiveNoteBasketItemQuantityRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v1/receive-note/${queryArg.receiveNoteId}/basket/item/${queryArg.itemId}/quantity`,
        method: 'PATCH',
        queryArg,
        body: queryArg.body,
      }),
      invalidatesTags: (result, error, queryArg) => [
        ...RECEIVE_NOTE_BASKET_ALL_TAGS(queryArg.receiveNoteId),
      ],
      extraOptions: {
        requestSchema: PatchReceiveNoteBasketItemQuantityRequestSchema,
        responseSchema: PatchReceiveNoteBasketItemQuantityResponseSchema,
      },
    }),
    bulkDeleteReceiveNoteBasketItems: build.mutation<
      BulkDeleteReceiveNoteBasketItemsResponse,
      BulkDeleteReceiveNoteBasketItemsRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v1/receive-note/${queryArg.receiveNoteId}/basket/item/delete`,
        method: 'PATCH',
        queryArg,
        body: queryArg.body,
      }),
      invalidatesTags: (result, error, queryArg) => [
        ...RECEIVE_NOTE_BASKET_ALL_TAGS(queryArg.receiveNoteId),
      ],
      extraOptions: {
        requestSchema: BulkDeleteReceiveNoteBasketItemsRequestSchema,
        responseSchema: BulkDeleteReceiveNoteBasketItemsResponseSchema,
      },
    }),
    deleteReceiveNoteBasket: build.mutation<
      DeleteReceiveNoteBasketResponse,
      DeleteReceiveNoteBasketRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v1/receive-note/${queryArg.receiveNoteId}/basket`,
        method: 'DELETE',
        queryArg,
      }),
      invalidatesTags: (result, error, queryArg) => getDeleteBasketTags(queryArg.receiveNoteId),
      extraOptions: {
        requestSchema: DeleteReceiveNoteBasketRequestSchema,
        responseSchema: DeleteReceiveNoteBasketResponseSchema,
      },
    }),
  }),
});

export const {
  useGetReceiveNoteBasketItemsQuery,
  useGetReceiveNoteBasketTotalPriceQuery,
  usePostReceiveNoteBasketCheckoutMutation,
  usePostReceiveNoteBasketItemMutation,
  usePatchReceiveNoteBasketItemQuantityMutation,
  useBulkDeleteReceiveNoteBasketItemsMutation,
  useDeleteReceiveNoteBasketMutation,
} = metadaWarehouseReceiveNoteBasketApi;
